/**
 * ポートの宣言の作り方（索引 Issue #498 の段階 4。設計文書 §7）。
 *
 * ポート = **コアが切り、外側が実装するインターフェース**。ここにあるのは
 *   1. どんなメソッドがあるかの宣言
 *   2. 何もしない既定の実装（**ポートが無くてもコアは動く**）
 *   3. 実装を渡したときの検め（知らないメソッド・関数でないものはここで分かる）
 * だけで、中身は一切持たない。実装は描画側・環境側（React / three / api / Web Audio / S3）にある。
 *
 * 「何もしない既定」を必ず用意するのは、**画面が片方だけ先に出来ている状態**を普通にするため
 * （テンプレートの保存先がまだ無くても編集はできる、音が無くても再生はできる）。
 */
const isFn = (v) => typeof v === 'function';

/**
 * @param name ポートの名前（エラーに出す）
 * @param methods メソッド名 → 何もしない既定の実装
 * @param fields メソッドではない項目（`id` など）の既定
 */
export function definePort(name, methods, fields = {}) {
  const names = Object.keys(methods);
  const nullPort = Object.freeze({ ...fields, ...methods, __port: name });

  /** 実装を検めて、足りないメソッドを既定で埋めた 1 つのポートにする */
  function create(impl = null) {
    if (impl == null) return nullPort;
    if (typeof impl !== 'object') throw new Error(`${name}: 実装はオブジェクトです`);
    const out = { ...fields, ...methods };
    for (const [k, v] of Object.entries(impl)) {
      if (names.includes(k)) {
        if (!isFn(v)) throw new Error(`${name}.${k} は関数です`);
        out[k] = v;
      } else if (Object.prototype.hasOwnProperty.call(fields, k)) {
        out[k] = v;
      } else {
        throw new Error(`${name}: 知らない項目です（${k}）。使えるのは ${[...names, ...Object.keys(fields)].join(' / ')}`);
      }
    }
    return { ...out, __port: name };
  }

  return { name, methods: names, fields: Object.keys(fields), null: nullPort, create, isNull: (p) => p === nullPort };
}

/** 購読の口を持つポートのための小さな道具（実装が自前で持たなくてよいように） */
export function createEmitter() {
  const listeners = new Set();
  return {
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    emit(...args) { listeners.forEach((fn) => fn(...args)); },
    get size() { return listeners.size; },
  };
}
